import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
} from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../config';
import { DataGridPro } from '@mui/x-data-grid-pro';

const columns = [
  { field: 'company_name', headerName: 'Company', minWidth: 220, flex: 1 },
  { field: 'company_address', headerName: 'Address', minWidth: 260, flex: 1 },
  { field: 'company_phone', headerName: 'Phone', minWidth: 160 },
  { field: 'contact_name', headerName: 'Contact Name', minWidth: 200 },
  { field: 'contact_email', headerName: 'Contact Email', minWidth: 240 },
];

const normalizePartner = (partner = {}, idx = 0) => ({
  id: partner.external_company_id || partner.id || partner.company_id || partner.company_guid || `partner-${idx}`,
  company_name: partner.company_name || partner.name || '—',
  company_address: partner.company_address || partner.address || partner.company_location || partner.location || '',
  company_phone: partner.company_phone || partner.phone || partner.contact_phone || '',
  contact_name: partner.contact_name || partner.company_contact_name || partner.primary_contact_name || '',
  contact_email: partner.contact_email || partner.email || partner.contactEmail || '',
});

const ReferralPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${APIURL}/referral/partners`);
      const list = Array.isArray(res.data) ? res.data : [];
      setPartners(list.map(normalizePartner));
    } catch (err) {
      console.error('Failed to load referral partners', err);
      setError('Unable to load referral partners.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const rows = useMemo(() => partners, [partners]);

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ minHeight: 0, height: 'calc(100vh - 150px)', backgroundColor: '#f5f5f5' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 1 ? 'report-row-alternate' : ''
          }
          initialState={{
            sorting: { sortModel: [{ field: 'company_name', sort: 'asc' }] },
            pinnedColumns: { left: [] },
          }}
          sx={{
            backgroundColor: '#fff',
            flex: 1,
            height: '100%',
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
              textAlign: 'left',
              wordBreak: 'break-word',
              maxWidth: '100%',
              fontWeight: 600,
            },
            '& .report-row-alternate .MuiDataGrid-cell, & .report-row-alternate .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#EAF4FC',
            },
            '& .report-row-alternate:hover .MuiDataGrid-cell, & .report-row-alternate:hover .MuiDataGrid-filler--pinnedLeft': {
              backgroundColor: '#DCEFFC',
            },
          }}
          showToolbar
          localeText={{
            noRowsLabel: 'No referral partners available.',
          }}
        />
      </Box>
    </>
  );
};

export default ReferralPartners;
